import { ContactFilterConditionInterface } from 'modules/contacts/types/contact-filter-interface'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { Dispatch, SetStateAction, useState } from 'react'
import { useDidUpdateEffect } from 'shared/hooks/use-did-update-effect'
import { QueryKeysEnum } from 'shared/route-query/enum/query-keys-enum'
import { useQuery } from 'shared/route-query/hooks/use-query'
import { getLimitQuery } from 'shared/route-query/utils/get-limit-query'
import { getPaginationQuery } from 'shared/route-query/utils/get-pagination-query'
import { setLimitFromQuery } from 'shared/route-query/utils/set-limit-from-query'
import { setPaginationFromQuery } from 'shared/route-query/utils/set-pagination-from-query'
import { LimitType, PaginationInterface } from 'shared/types/pagination-interface'

export const useContactFilterPagination = ({
  conditions,
  setConditions,
}: {
  conditions: ContactFilterConditionInterface[]
  setConditions: Dispatch<SetStateAction<ContactFilterConditionInterface[]>>
}) => {
  const { query } = useRouter()
  const [limit, setLimit] = useState<LimitType>(setLimitFromQuery(query))

  const [pagination, setPagination] = useState<PaginationInterface>(setPaginationFromQuery(query))

  const [memoizedQueryData, setMemoizedQueryData] = useState<ParsedUrlQuery>()

  useDidUpdateEffect(() => {
    const paginationQuery = getPaginationQuery(pagination)
    const limitQuery = getLimitQuery(limit)
    const filterQuery: Record<string, string> = {}
    if (conditions.length !== 0) {
      filterQuery['filter'] = JSON.stringify(conditions)
    }
    setMemoizedQueryData({ ...paginationQuery, ...limitQuery, ...filterQuery })
  }, [conditions, limit, pagination])

  useQuery({
    data: memoizedQueryData,
    onChange: query => {
      const limit = setLimitFromQuery(query)
      setLimit(limit)

      const pagination = setPaginationFromQuery(query)
      setPagination(pagination)

      const queryConditions = query['filter'] as string | undefined
      if (!queryConditions) {
        setConditions([])
        return
      }
      try {
        const conditions = JSON.parse(queryConditions)
        setConditions(conditions)
      } catch {
        setConditions([])
      }
    },
    keys: Object.values(QueryKeysEnum),
  })

  return { limit, setLimit, pagination, setPagination }
}
